<template>
  <UIAlert v-if="isAdult === false" variant="danger">
    We are unable to process your request at this time. Please
    <a href="/contact">contact us</a> for additional assistance.
  </UIAlert>

  <form v-else novalidate @submit="onSubmit">
    <slot></slot>

    <div class="d-flex gap-2">
      <UIButton
        v-if="hasPrevious"
        type="button"
        variant="link"
        :disabled="disabled || loading"
        @click="goBack"
      >
        Back
      </UIButton>

      <UIButton
        v-if="!isLastStep"
        type="submit"
        variant="success"
        :disabled="disabled || loading"
      >
        Next
      </UIButton>

      <div class="w-100" v-else>
        <div class="d-grid">
          <slot name="submit" :disabled="disabled" :loading="loading">
            <SubmitButton :disabled="disabled" :loading="loading" />
          </slot>
        </div>

        <div class="mt-2" v-if="recaptcha">
          <ReCaptchaTerms class="text-center" />
          <ReCaptcha ref="recaptchaRef" size="invisible" />
        </div>

        <UIAlert v-if="error" variant="danger">
          We are unable to process your request at this time. Please
          <a href="/contact">contact us</a> for additional assistance.
        </UIAlert>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
export type FormWizardProps = {
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  recaptcha?: boolean;
  validationSchema?: Schema[];
};

export type FormWizardEvents = {
  (event: "submit", values: GenericObject): unknown;
};

export type FormWizardContext = {
  currentStepIdx: Ref<number>;
  stepCounter: Ref<number>;
};

export const formWizardContextKey = "form-wizard";

export function useFormWizardContext(): FormWizardContext {
  return inject<FormWizardContext>(formWizardContextKey, {
    currentStepIdx: ref(0),
    stepCounter: ref(0),
  });
}
</script>

<script lang="ts" setup>
import UIButton from "@/components/UI/UIButton.vue";
import { GenericObject, useForm } from "vee-validate";
import { Ref, computed, inject, nextTick, provide, ref } from "vue";
import { Schema } from "yup";
import { useAgeVerification } from "../fields/AgeField.vue";
import ReCaptcha from "../recaptcha/ReCaptcha.vue";
import ReCaptchaTerms from "../recaptcha/ReCaptchaTerms.vue";
import UIAlert from "../UI/UIAlert.vue";
import SubmitButton from "./SubmitButton.vue";

const props = defineProps<FormWizardProps>();
const emit = defineEmits<FormWizardEvents>();

const currentStepIdx = ref<number>(0);
const stepCounter = ref<number>(0);
const recaptchaRef = ref<InstanceType<typeof ReCaptcha>>();

provide<FormWizardContext>(formWizardContextKey, {
  currentStepIdx,
  stepCounter,
});

const isLastStep = computed<boolean>(
  () => currentStepIdx.value === stepCounter.value - 1
);

const hasPrevious = computed<boolean>(() => currentStepIdx.value > 0);

const currentSchema = computed<Schema | undefined>(() => {
  return props.validationSchema?.[currentStepIdx.value];
});

const { handleSubmit } = useForm({
  validationSchema: currentSchema,
  keepValuesOnUnmount: false,
});

const isAdult = useAgeVerification();

const onSubmit = handleSubmit(onValidSubmit, console.log);

/**
 * Handler for valid form submission.
 *
 * - Moves to the next `FormStep` when present.
 * - Validate the re-captcha if in use.
 * - Emits `submit` with the form values on last `FormStep`.
 */
async function onValidSubmit(values: GenericObject) {
  if (values["adult"] === "no") {
    isAdult.value = false;
    return;
  }

  if (values) {
    if (!isLastStep.value) {
      currentStepIdx.value++;

      return;
    }
  }

  if (props.recaptcha) {
    try {
      values["g-recaptcha-response"] = await validateRecaptcha();
    } catch (error) {
      console.error("recaptcha error!", error);
      // If this is a user error, the recaptcha widget will display an alert.
      return;
    }
  }

  delete values["age-verification"];

  emit("submit", values);
}

/**
 *
 */
async function validateRecaptcha(): Promise<string> {
  if (!recaptchaRef.value) {
    throw "recaptcha widget is missing.";
  }

  // Reset any previous errors.
  recaptchaRef.value.error = null;

  // Get recaptcha token.
  const token = await recaptchaRef.value.execute();
  await nextTick();

  return token;
}

/**
 *
 */
function goBack() {
  if (hasPrevious.value) {
    currentStepIdx.value--;
  }
}
</script>
